<template>
  <div class="donation-page">
    <div v-if="profile.status === 'Available'" class="donation-container">
      <!-- Header Section -->
      <div class="header-section">
        <h1 class="page-title">{{ $t("donate") }}</h1>
        <v-btn
          icon
          @click="goToHistory"
          class="history-btn"
          aria-label="View donation history"
        >
          <v-icon color="white">mdi-history</v-icon>
          <v-tooltip bottom>View Donation History</v-tooltip>
        </v-btn>
      </div>

      <!-- Search Section -->
      <div class="search-section">
        <p class="search-prompt">
          Which nonprofit organization would you like to donate to?
        </p>

        <div class="search-container">
          <v-menu
            offset-y
            v-model="searchDropdown"
            :close-on-content-click="false"
            transition="slide-y-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="searchKey"
                hide-details
                placeholder="Search organizations..."
                solo
                flat
                dense
                clearable
                @click:clear="clearSearch"
                append-icon="mdi-magnify"
                class="search-field"
                elevation="0"
                v-bind="attrs"
                v-on="on"
                background-color="#F0EBFF"
                autocomplete="off"
                @keyup.enter="onSearch"
                @focus="searchDropdown = true"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#625E71">mdi-magnify</v-icon>
                </template>
              </v-text-field>
            </template>

            <v-card class="search-dropdown">
              <v-list class="search-results" v-if="recentSearches.length > 0">
                <v-subheader>Recent Searches</v-subheader>
                <!-- <v-list-item
                  v-for="(item, index) in recentSearches"
                  :key="index"
                  class="search-item"
                  ripple
                >
                  <v-list-item-icon>
                    <v-icon>mdi-clock-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content @click="onSelectKey(item.key)">
                    <v-list-item-title>{{ item.key }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click.stop="onDeleteKey(item)"
                      aria-label="Delete search history item"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item> -->
              </v-list>

              <v-list-item class="search-action-item" @click="onSearch" ripple>
                <v-list-item-icon>
                  <v-icon color="primary">mdi-magnify</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Search
                    {{ searchKey ? `for "${searchKey}"` : "organizations" }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item> 
            </v-card>
          </v-menu>
        </div>
      </div>

      <!-- Agencies Grid -->
      <div class="agencies-grid">
        <v-row class="justify-center">
          <v-col
            v-for="agency in agencies"
            :key="agency._id"
            cols="12"
            sm="6"
            md="4"
            class="justify-center d-flex"
          >
            <donation-agency-card
              :agency="agency"
              class="agency-card"
              @click.native="onSelected(agency)"
            />
          </v-col>
        </v-row>
      </div>
      <!-- Loading/Empty States -->
      <div class="state-container">
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          size="64"
          width="6"
          class="loading-spinner"
        />

        <v-card
          v-else-if="agencies.length === 0"
          class="empty-state"
          elevation="0"
        >
          <v-img
            :src="require('@/assets/svg/empty-post.svg')"
            contain
            class="empty-image"
          />
          <v-card-title class="empty-title"
            >No matching organizations found</v-card-title
          >
          <v-card-subtitle class="empty-subtitle">
            Try adjusting your search or explore different causes
          </v-card-subtitle>
          <v-btn
            color="primary"
            depressed
            @click="clearSearch"
            class="empty-action"
          >
            Clear Search
          </v-btn>
        </v-card>
      </div>
    </div>

    <!-- Pending/Rejected State -->
   <div v-else>      
      <div
    v-if="profile.status === 'Pending' || profile.status === 'Rejected'"
    class="status-message"
  >
    <v-card class="status-card">
      <v-card-text class="py-8 text-center">
        <v-icon size="64" color="warning">mdi-clock</v-icon>
        <h2 class="mt-4">Account Under Review</h2>
        <p class="mt-2 grey--text">
          Please wait for approval/confirmation from Admin
        </p>
        <v-btn color="primary" text class="mt-4" @click="$router.push('/')">
          Back to Home
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
    </div> 

    <!-- Error Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="5000" color="error" top right>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import DonationAgencyCard from "./DonationAgencyCard";

export default {
  components: { DonationAgencyCard },
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      loadCompleted: -1,
      loadingMore: false,
      searchDropdown: false,
      searchKey: "",
      recentSearches: []
    };
  },
  methods: {
    ...mapActions({
      fetchAllAgencies: "agency/fetchAllAgencies",
      getSearchContents: "search/getSearchContents",
      getSearchHistory: "search/getSearchHistory",
      deleteKey: "search/deleteKey"
    }),
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    loadedData() {
      this.loadCompleted++;
    },
    loadMoreAgencies() {
      this.loadingMore = true;
      this.loadMore({ skip: this.agencies.length })
        .then(() => {
          setTimeout(() => {
            this.loadingMore = false;
          }, 2500);
        })
        .catch(error => {
          this.loadingMore = false;
          if (error.response == undefined) {
            console.log(error);
          } else {
            this.showErrorMessage(error.response.data.message);
          }
        });
    },
    getCompletedInfo(agency) {
      const missingFields = [];
      if (!agency.image) missingFields.push("Logo");
      if (!agency.who_we_are) missingFields.push("Who we are");
      if (!agency.what_we_do) missingFields.push("What we do");
      if (!agency.contact_person || !agency.contact_title)
        missingFields.push("Contact Info");
      if (!agency.phone) missingFields.push("Phone");
      if (!agency.address) missingFields.push("Address");
      if (!agency.city) missingFields.push("City");
      if (!agency.state) missingFields.push("State");
      if (!agency.zip) missingFields.push("Zip");

      return missingFields.join(", ");
    },
    onSelected(agency) {
      console.log("Selected agency:", agency);
    },
    onSearch() {
      this.searchDropdown = false;
      if (this.searchKey !== this.$route.query.search) {
        this.$router.replace({ query: { search: this.searchKey } });
      }
    },
    clearSearch() {
      this.searchKey = "";
      this.$router.replace({ query: {} });
    },
    onSelectKey(key) {
      this.searchKey = key;
      this.searchDropdown = false;
      this.onSearch();
    },
    onDeleteKey(key) {
      this.deleteKey({ _id: key._id })
        .then(res => {
          this.recentSearches = this.recentSearches.filter(
            r => r._id !== res._id
          );
        })
        .catch(error => {
          console.error(
            "Error deleting search key:",
            error.response?.data?.message || error.message
          );
        });
    },
    onLoadAgencies() {
      this.loading = true;
      this.loadCompleted = 0;
      this.fetchAllAgencies({ search: this.$route.query.search })
        .then(() => {
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          this.showErrorMessage(
            error.response?.data?.message || "Failed to load organizations"
          );
        });
    },
    goToHistory() {
      this.$router.push({ name: "donation-history" });
    }
  },
  computed: {
    ...mapGetters("agency", {
      agencies: "getAllAgencies"
    }),
    ...mapState("auth", {
      profile: "profile",
      type: "type"
    })
  },
  watch: {
    searchDropdown(newValue) {
      if (newValue) {
        this.getSearchHistory({ type: "Agency" })
          .then(res => {
            this.recentSearches = res;
          })
          .catch(error => {
            console.error("Error fetching search history:", error);
          });
      }
    },
    "$route.query.search": function(newVal) {
      if (newVal !== this.searchKey) {
        this.searchKey = newVal || "";
      }
      this.onLoadAgencies();
    }
  },
  mounted() {
    this.onLoadAgencies();
    this.searchKey = this.$route.query.search || "";
  }
};
</script>

<style scoped lang="scss">
.donation-page {
  width: 100%;
  min-height: 100vh;
  padding: 24px;
  display: flex;
  justify-content: center;
}

.donation-container {
  width: 100%;
  max-width: 1200px;
  padding: 24px;
  animation: fadeIn 0.5s ease-out;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  .page-title {
    color: white;
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0;
    background: linear-gradient(90deg, #ffffff 0%, #d6cdf7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .history-btn {
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.1);
    }
  }
}

.search-section {
  margin-bottom: 40px;

  .search-prompt {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.125rem;
    margin-bottom: 16px;
  }

  .search-container {
    position: relative;

    .search-field {
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
      }

      &::v-deep .v-input__slot {
        padding: 0 16px;
      }
    }
  }

  .search-dropdown {
    border-radius: 12px;
    margin-top: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    width: 100%;

    .search-results {
      max-height: 300px;
      overflow-y: auto;

      .search-item {
        transition: background-color 0.2s ease;

        &:hover {
          background-color: #f5f3ff;
        }
      }
    }

    .search-action-item {
      background-color: #f5f3ff;
      font-weight: 600;
    }
  }
}

.agencies-grid {
  width: 100%;

  .grid-container {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .agency-col {
    padding: 8px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }
  }

  .agency-card {
    height: 100%;
    transition: all 0.3s ease;
    cursor: pointer;
    width: 100%;
    max-width: 320px;
  }
}

.state-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;

  .loading-spinner {
    margin: 40px 0;
  }

  .empty-state {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 40px;
    text-align: center;
    width: 100%;
    max-width: 500px;

    .empty-image {
      width: 120px;
      height: 120px;
      margin: 0 auto 24px;
      opacity: 0.7;
    }

    .empty-title {
      color: white;
      font-size: 1.5rem;
      font-weight: 600;
      justify-content: center;
    }

    .empty-subtitle {
      color: rgba(255, 255, 255, 0.7);
      font-size: 1rem;
      margin-bottom: 24px;
    }

    .empty-action {
      margin-top: 16px;
    }
  }
}

.status-message {
  width: 100%;
  max-width: 600px;
  margin-top: 60px;

  .status-card {
    background: #fff;
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 32px;
    text-align: center;

    .status-title {
      color: white;
      font-size: 1.75rem;
      font-weight: 600;
      justify-content: center;
    }

    .status-text {
      color: rgba(255, 255, 255, 0.8);
      font-size: 1.1rem;
      margin-top: 16px;
    }
  }
}

/* Animations */
.fade-stagger-move {
  transition: transform 0.5s;
}

.fade-stagger-enter-active,
.fade-stagger-leave-active {
  transition: all 0.5s ease;
}

.fade-stagger-enter-from,
.fade-stagger-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .donation-container {
    padding: 16px;
  }

  .header-section .page-title {
    font-size: 2rem;
  }
}

@media (max-width: 600px) {
  .donation-page {
    padding: 16px;
  }

  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;

    .history-btn {
      align-self: flex-end;
    }
  }

  .search-section .search-prompt {
    font-size: 1rem;
  }

  .status-message .status-card {
    padding: 24px 16px;

    .status-title {
      font-size: 1.5rem;
    }
  }
}
</style>
